/**
 * src/lib/format.ts — presentation-layer formatting (§8.3). All rounding happens here;
 * the engine carries full float64 precision. Supports the "today's dollars" toggle (§6.19).
 */

const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const currencyFmtCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Format a USD amount (whole dollars by default). */
export function fmtCurrency(n: number, cents = false): string {
  if (!Number.isFinite(n)) return '—';
  return (cents ? currencyFmtCents : currencyFmt).format(n);
}

/** Format a percent value already expressed in percent units (e.g. 6.53 → "6.53%"). */
export function fmtPct(n: number, decimals = 2): string {
  if (!Number.isFinite(n)) return 'n/a';
  return `${n.toFixed(decimals)}%`;
}

/** Format a 0..1 ratio as a percent (e.g. 0.857 → "85.71%"). Infinite ⇒ "n/a". */
export function fmtRatioPct(n: number, decimals = 2): string {
  if (!Number.isFinite(n)) return 'n/a';
  return `${(n * 100).toFixed(decimals)}%`;
}

/** Convert a nominal amount to real (today's) dollars at month m (§6.19). */
export function realValue(nominal: number, monthIndex: number, inflationAnnualPct: number): number {
  return nominal / Math.pow(1 + inflationAnnualPct / 100, monthIndex / 12);
}

/** Apply the real-dollar toggle to a currency value before display. */
export function toDisplay(
  nominal: number,
  monthIndex: number,
  showRealDollars: boolean,
  inflationAnnualPct: number,
): number {
  return showRealDollars ? realValue(nominal, monthIndex, inflationAnnualPct) : nominal;
}

/** Compact currency for chart axes (e.g. $1.2M, $450k). */
export function fmtCurrencyCompact(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(abs >= 100_000 ? 0 : 0)}k`;
  return `${sign}$${abs.toFixed(0)}`;
}
